import { useEffect, useRef, useState } from 'react';
import { Loader2, X, Plus, ImagePlus, Camera, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/useIsMobile';
import StoryCameraCapture from './StoryCameraCapture';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const FILE_MAX = 50 * 1024 * 1024;
const VIDEO_MAX = 60;

/** Instagram-style story rail + viewer.
 *  - Pulls non-expired stories grouped by user from /api/social/stories/feed
 *  - Owner sees a "+ Your Story" tile to add (vendors + Founding Non-Profits only)
 *  - Tap a tile → fullscreen viewer with progress bar + auto-advance */
export default function StoryRail({ viewer, canPost }) {
  const [rails, setRails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);   // user-rail index
  const [sheetOpen, setSheetOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef(null);
  const isMobile = useIsMobile();

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/social/stories/feed`, {
        headers: viewer ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {},
      });
      if (res.ok) setRails((await res.json()).rails || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [viewer]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > FILE_MAX) return toast.error('File too large (50MB max)');
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) return toast.error('Image or video only');
    if (isVideo) {
      const dur = await new Promise((res) => {
        const v = document.createElement('video');
        v.preload = 'metadata';
        v.src = URL.createObjectURL(file);
        v.onloadedmetadata = () => res(v.duration || 0);
        v.onerror = () => res(0);
      });
      if (dur > VIDEO_MAX) { toast.error(`Story videos must be ≤${VIDEO_MAX}s`); return; }
      var duration = dur;
    }
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    toast.loading('Uploading story…', { id: 'story' });
    const fd = new FormData();
    fd.append('file', file);
    const upRes = await fetch(`${API}/media/upload?category=story`, { method: 'POST', headers, body: fd });
    const upData = await upRes.json().catch(() => ({}));
    if (!upRes.ok) { toast.error(upData.detail || 'Upload failed', { id: 'story' }); return; }
    const url = upData.url?.startsWith('http') ? upData.url : `${process.env.REACT_APP_BACKEND_URL}${upData.url}`;
    const res = await fetch(`${API}/social/stories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({
        media_url: url,
        media_type: isVideo ? 'video' : 'image',
        duration_seconds: typeof duration !== 'undefined' ? duration : null,
      }),
    });
    if (res.ok) { toast.success('Story posted! Lives 24h.', { id: 'story' }); load(); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Could not post', { id: 'story' }); }
  };

  if (loading) return null;
  if (rails.length === 0 && !canPost) return null;

  return (
    <div className="mb-6" data-testid="story-rail">
      <div className="flex items-center gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory">
        {/* Your Story tile */}
        {canPost && (
          <button
            onClick={() => (isMobile ? setSheetOpen(true) : fileRef.current?.click())}
            className="snap-start flex-shrink-0 flex flex-col items-center gap-1.5"
            data-testid="story-add"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-cyan-500/30 border-2 border-dashed border-fuchsia-400 flex items-center justify-center hover:bg-fuchsia-500/20">
              {viewer?.profile_image ? (
                <img src={viewer.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <Plus size={20} className="text-fuchsia-300" />
              )}
            </div>
            <span className="text-[11px] text-zinc-300 font-semibold">Your Story</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*,video/*,.heic,.heif" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />

        {rails.map((r, idx) => (
          <button
            key={r.user_id}
            onClick={() => setOpenIdx(idx)}
            className="snap-start flex-shrink-0 flex flex-col items-center gap-1.5"
            data-testid={`story-rail-${r.user_id}`}
          >
            <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-fuchsia-500 via-pink-400 to-yellow-400">
              <div className="w-full h-full rounded-full bg-[#0B0D2B] p-[2px]">
                {r.profile_image ? (
                  <img src={r.profile_image} alt={r.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <div className="w-full h-full rounded-full bg-fuchsia-500/30 flex items-center justify-center text-white font-bold">
                    {r.name[0]}
                  </div>
                )}
              </div>
            </div>
            <span className="text-[11px] text-zinc-300 font-semibold max-w-[68px] truncate">{r.is_self ? 'You' : r.name}</span>
          </button>
        ))}
      </div>

      {openIdx !== null && (
        <StoryViewer
          rails={rails}
          startIdx={openIdx}
          onClose={() => { setOpenIdx(null); load(); }}
        />
      )}

      {/* Mobile: choose between in-app camera and upload from library */}
      {sheetOpen && (
        <div
          className="fixed inset-0 z-[75] bg-black/60 backdrop-blur-sm flex items-end"
          onClick={() => setSheetOpen(false)}
          data-testid="story-source-sheet"
        >
          <div
            className="w-full bg-[#13153A] rounded-t-3xl border-t border-white/10 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3 px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />
            <p className="text-white text-center font-syne font-bold text-base mb-4">Add to your story</p>
            <button
              onClick={() => { setSheetOpen(false); setCameraOpen(true); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-fuchsia-500/25 via-pink-500/20 to-amber-400/15 border border-fuchsia-400/40 text-white mb-2.5 shadow-[0_0_24px_rgba(236,72,153,0.22)]"
              data-testid="story-source-camera"
            >
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Camera size={18} />
              </span>
              <span className="flex-1 text-left">
                <span className="block font-bold text-sm">Open camera</span>
                <span className="block text-[11px] text-white/70">Tap for photo · hold for video (60s)</span>
              </span>
            </button>
            <button
              onClick={() => { setSheetOpen(false); fileRef.current?.click(); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white mb-1"
              data-testid="story-source-upload"
            >
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Upload size={18} />
              </span>
              <span className="flex-1 text-left">
                <span className="block font-bold text-sm">Upload from library</span>
                <span className="block text-[11px] text-white/60">Photo or video, up to 50MB</span>
              </span>
            </button>
            <button
              onClick={() => setSheetOpen(false)}
              className="w-full mt-3 py-3 rounded-2xl text-zinc-400 text-sm font-semibold hover:text-white"
              data-testid="story-source-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* In-app Instagram-style camera */}
      {cameraOpen && (
        <StoryCameraCapture
          onClose={() => setCameraOpen(false)}
          onPosted={load}
        />
      )}
    </div>
  );
}

function StoryViewer({ rails, startIdx, onClose }) {
  const [railIdx, setRailIdx] = useState(startIdx);
  const [storyIdx, setStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const rail = rails[railIdx];
  const story = rail?.stories?.[storyIdx];

  useEffect(() => {
    if (!story) return undefined;
    setProgress(0);
    fetch(`${API}/social/stories/${story.story_id}/view`, { method: 'POST' }).catch(() => {});
    const dur = story.media_type === 'video' ? Math.min(story.duration_seconds || 8, 60) * 1000 : 5000;
    const start = Date.now();
    const tick = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / dur) * 100);
      setProgress(p);
      if (p >= 100) {
        clearInterval(tick);
        next();
      }
    }, 80);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railIdx, storyIdx]);

  const next = () => {
    if (storyIdx + 1 < rail.stories.length) setStoryIdx(storyIdx + 1);
    else if (railIdx + 1 < rails.length) { setRailIdx(railIdx + 1); setStoryIdx(0); }
    else onClose();
  };
  const prev = () => {
    if (storyIdx > 0) setStoryIdx(storyIdx - 1);
    else if (railIdx > 0) { setRailIdx(railIdx - 1); setStoryIdx(rails[railIdx - 1].stories.length - 1); }
  };

  const remove = async () => {
    if (!confirm('Delete this story?')) return;
    const res = await fetch(`${API}/social/stories/${story.story_id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (res.ok) { toast.success('Deleted'); onClose(); }
  };

  if (!story) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="story-viewer">
      {/* Progress bars */}
      <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
        {rail.stories.map((s, i) => (
          <div key={s.story_id} className="flex-1 h-0.5 bg-white/30 rounded overflow-hidden">
            <div
              className="h-full bg-white"
              style={{ width: i < storyIdx ? '100%' : i === storyIdx ? `${progress}%` : '0%' }}
            />
          </div>
        ))}
      </div>
      {/* Header */}
      <div className="absolute top-7 left-3 right-3 flex items-center justify-between text-white text-xs z-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          {rail.profile_image && <img src={rail.profile_image} alt="" className="w-7 h-7 rounded-full object-cover border border-white/30" />}
          <span className="font-semibold">{rail.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {rail.is_self && (
            <button onClick={remove} className="p-2 rounded-full bg-white/10 hover:bg-red-500/30" data-testid="story-delete"><X size={14} /></button>
          )}
          <button onClick={onClose} className="p-2 rounded-full bg-white/10" data-testid="story-close"><X size={14} /></button>
        </div>
      </div>

      {/* Media */}
      <div className="relative max-w-md w-full max-h-[90vh] aspect-[9/16] bg-black flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {story.media_type === 'video' ? (
          <video src={story.media_url} className="w-full h-full object-contain" autoPlay muted playsInline onEnded={next} />
        ) : (
          <img src={story.media_url} alt="" className="w-full h-full object-contain" />
        )}
        {story.caption && (
          <p className="absolute bottom-6 left-3 right-3 text-white text-sm bg-black/40 rounded-lg p-3 backdrop-blur-sm">
            {story.caption}
          </p>
        )}
        <button className="absolute left-0 top-0 bottom-0 w-1/3" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous" />
        <button className="absolute right-0 top-0 bottom-0 w-1/3" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next" />
      </div>
    </div>
  );
}
